import React from 'react';
import ReactDOM from 'react-dom/client';
import reportWebVitals from './reportWebVitals';
import { AppContainer } from './app';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfigProvider } from 'antd';
import { HelmetProvider } from 'react-helmet-async';

/* Styles */
import 'styles/App.css';
import 'styles/index.css';
import 'styles/font.css';
import 'styles/antd.css';
import 'styles/colors.css';

/**
 * 구글 번역이 변경한 DOM과 React 렌더링 간의 노드 충돌 보정
 * --
 * 구글 번역은 React가 관리하는 텍스트 노드를 font 태그 등으로 직접
 * 교체합니다. 이후 React가 변경 전 노드를 기준으로 렌더링하면
 * insertBefore/removeChild에서 NotFoundError가 발생할 수 있습니다.
 *
 * 일반 DOM 처리에는 영향을 주지 않도록 구글 번역이 활성화되어 있고,
 * React의 root 내부에서 부모-자식 관계가 이미 변경된 경우에만 보정합니다.
 */
const isGoogleTranslated = () =>
  document.documentElement.classList.contains('translated-ltr') ||
  document.documentElement.classList.contains('translated-rtl') ||
  document.cookie.includes('googtrans=');

const isInsideReactRoot = (node: Node) => {
  const reactRoot = document.getElementById('root');
  return Boolean(reactRoot && (node === reactRoot || reactRoot.contains(node)));
};

const originalInsertBefore = Node.prototype.insertBefore;
Node.prototype.insertBefore = function <T extends Node>(
  newNode: T,
  referenceNode: Node | null,
): T {
  if (
    isGoogleTranslated() &&
    isInsideReactRoot(this) &&
    referenceNode &&
    referenceNode.parentNode !== this
  ) {
    // 기준 노드가 다른 부모로 이동된 경우 현재 부모의 마지막에 삽입합니다.
    return originalInsertBefore.call(this, newNode, null) as T;
  }

  return originalInsertBefore.call(this, newNode, referenceNode) as T;
};

const originalRemoveChild = Node.prototype.removeChild;
Node.prototype.removeChild = function <T extends Node>(child: T): T {
  if (
    isGoogleTranslated() &&
    isInsideReactRoot(this) &&
    child.parentNode !== this
  ) {
    // 이미 현재 부모에서 제거된 노드는 삭제가 완료된 것으로 처리합니다.
    return child;
  }

  return originalRemoveChild.call(this, child) as T;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // staleTime: Infinity,
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);
root.render(
  // <React.StrictMode>
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <ConfigProvider>
        <AppContainer />
      </ConfigProvider>
    </HelmetProvider>
    {process.env.REACT_APP_DEPLOY_TYPE !== 'production' && (
      <div style={{ fontSize: 16 }}>
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition='bottom-right'
        />
      </div>
    )}
  </QueryClientProvider>,
  // </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
